/* eslint-disable max-lines, max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import lottie from 'lottie-web';
import { TableUtility } from '@utility/dynamic-table';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { toast } from 'react-toastify';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ReactComponent as DownloadExcelIcon } from '../../../Assets/images/svg/dar_collection_dashboard_download.svg';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import { useAuth } from '../../../Hooks/useAuth';
import FormDialog from '../../../Components/Modal/modal';
import CommonTextField from '../../../Components/TextField/textField';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import SimpleSnackbar from '../../../Components/Snackbar/snackbar';
import UseAxios from '../../../Api/http/http';
import { MODULE_NAMES } from '../../../Constants/modules';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';
import {
  deleteConfigureRole,
  exportRoleRatioReport,
  getRoleRatioFilterOptionUrl,
  getRoleRatioFilterOptions,
  getConfigureRole,
  getRoleRatioFilters,
  getRoleRatioReportBaseURL,
  ROLE_RATIO_FILTER_TYPES,
  saveConfigureRole,
} from '../../../Api/request/roleRatioReport';
import {
  exportRoleRatioWorkbook,
  getRoleRatioFiltersFromTableState,
  hasRoleRatioRole1Filter,
  mergeRoleRatioFilterSelections,
} from './roleRatioExport';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './RoleRatioReport.scss';

const PAGE_SIZE = 10;
const baseURL = getRoleRatioReportBaseURL();
const ROLE_RATIO_MODULE = MODULE_NAMES.MIS_ROLE_RATIO_REPORT;

const getRoleRatioPermissionActions = (permissions = {}) => {
  const moduleCandidates = [
    ROLE_RATIO_MODULE,
    ROLE_RATIO_MODULE.replace(/-/g, '_'),
    ROLE_RATIO_MODULE.replace(/_/g, '-'),
  ];

  const permissionKeys = Object.keys(permissions || {});
  const permissionActions = moduleCandidates.flatMap((moduleName) => {
    const actions = permissions?.[moduleName];
    return Array.isArray(actions) ? actions : [];
  });
  const suffixedActions = moduleCandidates.flatMap((moduleName) => (
    permissionKeys
      .filter((permissionKey) => (
        permissionKey.startsWith(`${moduleName}_`)
        || permissionKey.startsWith(`${moduleName}:`)
      ))
      .map((permissionKey) => permissionKey.slice(moduleName.length + 1))
  ));

  return [...permissionActions, ...suffixedActions];
};

const normalizeRoleRatioPermissions = (permissions = {}) => {
  const normalizedActions = new Set(
    getRoleRatioPermissionActions(permissions).map((action) => String(action).toLowerCase()),
  );

  if (normalizedActions.has('view')) normalizedActions.add('read');
  if (normalizedActions.has('edit')) normalizedActions.add('update');
  if (normalizedActions.has('update')) normalizedActions.add('read');

  return {
    ...permissions,
    [ROLE_RATIO_MODULE]: [...normalizedActions],
  };
};

const hasRoleRatioAction = (permissions = {}, actions = []) => {
  const normalizedPermissions = normalizeRoleRatioPermissions(permissions);
  const moduleActions = normalizedPermissions[ROLE_RATIO_MODULE] || [];
  return actions.some((action) => moduleActions.includes(action));
};

const addRoleRatioFilterUrls = (tableConfiguration = [], useRemoteFilterOptions = true) => (
  (Array.isArray(tableConfiguration) ? tableConfiguration : []).map((column) => ({
    ...column,
    menuItems: (Array.isArray(column?.menuItems) ? column.menuItems : []).map((menuItem) => ({
      ...menuItem,
      items: (Array.isArray(menuItem?.items) ? menuItem.items : []).map((item) => {
        if (!useRemoteFilterOptions) {
          const { url: __url, ...itemWithoutUrl } = item;
          return itemWithoutUrl;
        }

        const url = getRoleRatioFilterOptionUrl(item?.field);
        if (item?.type !== 'filter' || !url) {
          return item;
        }

        return {
          ...item,
          url,
        };
      }),
    })),
  }))
);

const normalizeRoleRatioTableConfig = (
  tableConfiguration = [],
  tableData = [],
  useRemoteFilterOptions = true,
) => {
  const sourceTableConfiguration = Array.isArray(tableConfiguration) ? tableConfiguration : [];
  const explicitSortableFields = new Set(
    sourceTableConfiguration
      .filter((column) => column?.sortable === true)
      .map((column) => column?.field)
      .filter(Boolean),
  );
  const response = {
    data: {
      tableConfiguration: sourceTableConfiguration,
      tableData: Array.isArray(tableData) ? tableData : [],
    },
  };

  normalizeTableUtilityResponse(response);

  return addRoleRatioFilterUrls((response.data.tableConfiguration || []).map((column) => ({
    ...column,
    sortable: explicitSortableFields.has(column?.field),
    menuItems: (Array.isArray(column?.menuItems) ? column.menuItems : []).map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items) ? menuItem.items : [],
    })),
  })), useRemoteFilterOptions);
};

const isMissingFilterOptionsRoute = (response) => {
  const status = response?.status || response?.data?.response?.status;
  const responseText = String(response?.data?.response?.data || response?.data?.message || '');

  return status === 404 || responseText.includes('Cannot POST');
};

const getFilterOptionItemsByField = (tableConfiguration = []) => {
  const optionItemsByField = new Map();

  (Array.isArray(tableConfiguration) ? tableConfiguration : []).forEach((column) => {
    (Array.isArray(column?.menuItems) ? column.menuItems : []).forEach((menuItem) => {
      (Array.isArray(menuItem?.items) ? menuItem.items : []).forEach((item) => {
        if (item?.type !== 'filter' || !item?.field) return;
        if (!Array.isArray(item.options) || item.options.length === 0) return;
        optionItemsByField.set(item.field, {
          options: item.options,
          values: Array.isArray(item.values) && item.values.length ? item.values : item.options,
        });
      });
    });
  });

  return optionItemsByField;
};

const preserveRoleRatioFilterOptions = (nextTableConfiguration = [], currentTableConfiguration = []) => {
  const optionItemsByField = getFilterOptionItemsByField(currentTableConfiguration);

  if (!optionItemsByField.size) return nextTableConfiguration;

  return (Array.isArray(nextTableConfiguration) ? nextTableConfiguration : []).map((column) => ({
    ...column,
    menuItems: (Array.isArray(column?.menuItems) ? column.menuItems : []).map((menuItem) => ({
      ...menuItem,
      items: (Array.isArray(menuItem?.items) ? menuItem.items : []).map((item) => {
        const preservedOptions = optionItemsByField.get(item?.field);
        if (
          item?.type !== 'filter'
          || !preservedOptions
          || (Array.isArray(item.options) && item.options.length > 0)
        ) {
          return item;
        }

        return {
          ...item,
          ...preservedOptions,
        };
      }),
    })),
  }));
};

const EmptyState = React.memo(({ message }) => {
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);

  useEffect(() => {
    let isMounted = true;
    import('../../../Assets/images/json/dar_no_data.json').then(({ default: data }) => {
      if (isMounted) setAnimationData(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!animationData || !animationContainer.current) return undefined;
    const anim = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData,
    });
    return () => anim.destroy();
  }, [animationData]);

  return (
    <div className="role-ratio-empty-state">
      <div className="role-ratio-empty-card">
        <div ref={animationContainer} className="role-ratio-empty-animation" />
        <p className="role-ratio-empty-text">{message}</p>
      </div>
    </div>
  );
});

EmptyState.propTypes = { message: PropTypes.string.isRequired };
EmptyState.displayName = 'EmptyState';

const RoleRatioMoreActionsMenu = ({ onViewRole }) => {
  const moreMenuRef = useRef(null);

  const menuItems = useMemo(() => [
    {
      label: 'View Role',
      command: onViewRole,
    },
  ], [onViewRole]);

  return (
    <React.Fragment>
      <Menu
        ref={moreMenuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        className="role-ratio-more-menu-popup"
      />
      <Button
        type="button"
        icon="pi pi-ellipsis-v"
        className="p-button-text role-ratio-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        aria-label="More Actions"
      />
    </React.Fragment>
  );
};

RoleRatioMoreActionsMenu.propTypes = { onViewRole: PropTypes.func.isRequired };

const toDropdownOptions = (rows = [], idKey, nameKey) => rows.map((row) => ({
  id: String(row[idKey]),
  name: row[nameKey],
  raw: row,
}));

const ConfigureRoleDialog = ({
  open,
  onClose,
  selectedRoleId,
  onSaved,
  userInfo,
}) => {
  const [loading, setLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [roleId, setRoleId] = useState(selectedRoleId || '');
  const [roleName, setRoleName] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadConfigureRole = useCallback(async (nextRoleId = '') => {
    const response = await UseAxios(getConfigureRole(nextRoleId, userInfo), setLoading);
    const data = response?.data?.data || {};
    setDepartmentOptions(toDropdownOptions(data.departments || [], 'departmentId', 'departmentName'));
    setDesignationOptions(toDropdownOptions(data.designations || [], 'designationId', 'designationName'));
    const mappedRows = (data.roleDetails || []).map((item) => ({
      id: item.id,
      roleId: String(item.roleId),
      roleName: item.roleName,
      departmentId: String(item.departmentId),
      departmentName: item.departmentName,
      designationId: String(item.designationId),
      designationName: item.designationName,
    }));
    setAllRows(mappedRows);
    const scopedRows = nextRoleId
      ? mappedRows.filter((row) => String(row.roleId) === String(nextRoleId))
      : [];
    setRows(scopedRows);
    const firstDetail = scopedRows[0];
    if (firstDetail && nextRoleId) {
      setRoleId(String(firstDetail.roleId));
      setRoleName(firstDetail.roleName || '');
    } else if (!nextRoleId) {
      setRoleId('');
      setRoleName('');
    }
    setSelectedDepartment(null);
    setSelectedDesignation(null);
    setEditingIndex(null);
  }, [userInfo]);

  useEffect(() => {
    if (open) {
      setError('');
      loadConfigureRole(selectedRoleId || '');
    }
  }, [loadConfigureRole, open, selectedRoleId]);

  const validateCombination = () => {
    if (!roleName.trim()) return 'Role is required';
    if (!selectedDepartment?.id) return 'Department is required';
    if (!selectedDesignation?.id) return 'Designation is required';
    const duplicate = rows.some((row, index) => (
      index !== editingIndex
      && row.departmentId === selectedDepartment.id
      && row.designationId === selectedDesignation.id
    ));
    if (duplicate) return 'Department and Designation combination already exists';
    return '';
  };

  const handleAddOrUpdateRow = () => {
    const validationError = validateCombination();
    if (validationError) {
      setError(validationError);
      return;
    }

    const nextRow = {
      roleId,
      roleName: roleName.trim(),
      departmentId: selectedDepartment.id,
      departmentName: selectedDepartment.name,
      designationId: selectedDesignation.id,
      designationName: selectedDesignation.name,
    };

    setRows((prev) => {
      if (editingIndex === null) return [...prev, nextRow];
      return prev.map((row, index) => (index === editingIndex ? nextRow : row));
    });
    setSelectedDepartment(null);
    setSelectedDesignation(null);
    setEditingIndex(null);
    setError('');
  };

  const handleEdit = (row, index) => {
    setRoleId(String(row.roleId || roleId));
    setRoleName(row.roleName || roleName);
    setSelectedDepartment(departmentOptions.find((item) => item.id === row.departmentId) || null);
    setSelectedDesignation(designationOptions.find((item) => item.id === row.designationId) || null);
    setEditingIndex(index);
    setError('');
  };

  const handleDelete = (row, index) => {
    if (roleId && rows.length > 1) {
      setRows((prev) => prev.filter((rowItem, rowIndex) => rowIndex !== index));
      return;
    }

    if (row.roleId) {
      setDeleteConfirm({ row, index });
      return;
    }

    setRows((prev) => prev.filter((rowItem, rowIndex) => rowIndex !== index));
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm?.row?.roleId) return;

    const response = await UseAxios(deleteConfigureRole(deleteConfirm.row.roleId, userInfo), setLoading);
    if (response?.data?.success) {
      onSaved(response.data.message || 'Role deleted successfully');
      setDeleteConfirm(null);
      loadConfigureRole('');
    } else {
      setError(response?.data?.message || 'Unable to delete role');
    }
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      setError('Role is required');
      return;
    }
    if (!rows.length) {
      setError('At least one department and designation is required');
      return;
    }

    const response = await UseAxios(saveConfigureRole({
      roleId: roleId || null,
      roleName: roleName.trim(),
      combinations: rows.map((row) => ({
        departmentId: row.departmentId,
        designationId: row.designationId,
      })),
    }, userInfo), setLoading);

    if (response?.data?.success) {
      onSaved(response.data.message || 'Role saved successfully');
      onClose();
    } else {
      setError(response?.data?.message || response?.data?.data?.message || 'Unable to save role');
    }
  };

  const displayRows = roleId || roleName ? rows : allRows;

  const body = (
    <div className="configure-role-body">
      <CommonTextField
        label="Role*"
        placeholder="Enter Role"
        value={roleName}
        onChange={(event) => {
          setRoleName(event.target.value);
          setError('');
        }}
        variant="outlined"
        autoFocus={false}
        size="small"
      />
      <div className="configure-role-form-row">
        <SearchDropdown
          label="Department*"
          placeholder="Select"
          options={departmentOptions}
          value={selectedDepartment}
          onChange={(event) => {
            setSelectedDepartment(event.target.value);
            setError('');
          }}
        />
        <SearchDropdown
          label="Designation*"
          placeholder="Select"
          options={designationOptions}
          value={selectedDesignation}
          onChange={(event) => {
            setSelectedDesignation(event.target.value);
            setError('');
          }}
        />
      </div>
      <div className="configure-role-form-actions">
        <button type="button" className="configure-role-inline-btn" onClick={() => {
          setSelectedDepartment(null);
          setSelectedDesignation(null);
          setEditingIndex(null);
          setError('');
        }}>
          Cancel
        </button>
        <button type="button" className="configure-role-inline-btn primary" onClick={handleAddOrUpdateRow}>
          {editingIndex === null ? 'Add' : 'Update'}
        </button>
      </div>
      {error ? <p className="configure-role-error">{error}</p> : null}
      <h3 className="configure-role-table-title">Already Existing Role Names</h3>
      <div className="configure-role-table-wrap">
        <table className="configure-role-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length ? displayRows.map((row, index) => {
              const isEditing = editingIndex === index;
              return (
                <tr
                  key={`${row.departmentId}-${row.designationId}-${index}`}
                  className={isEditing ? 'configure-role-editing-row' : ''}
                >
                  <td>{roleName || row.roleName}</td>
                  <td>{row.departmentName}</td>
                  <td>{row.designationName}</td>
                  <td className="configure-role-action-cell">
                    <div className="configure-role-action-content">
                      {isEditing ? (
                        <span className="configure-role-editing-tag">Editing...</span>
                      ) : (
                        <React.Fragment>
                          <button
                            type="button"
                            className="configure-role-icon-btn"
                            onClick={() => {
                              const scopedRows = allRows.filter((item) => item.roleId === row.roleId);
                              const scopedIndex = scopedRows.findIndex((item) => (
                                item.id === row.id
                                || (
                                  item.departmentId === row.departmentId
                                  && item.designationId === row.designationId
                                  && item.roleId === row.roleId
                                )
                              ));
                              if (scopedRows.length) setRows(scopedRows);
                              handleEdit(row, scopedIndex === -1 ? index : scopedIndex);
                            }}
                            title="Edit"
                          >
                            <EditOutlinedIcon fontSize="inherit" />
                          </button>
                          <button
                            type="button"
                            className="configure-role-icon-btn"
                            onClick={() => handleDelete(row, index)}
                            title="Delete"
                          >
                            <DeleteOutlineIcon fontSize="inherit" />
                          </button>
                        </React.Fragment>
                      )}
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4">No role configuration added</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {loading ? <span className="configure-role-error">Loading...</span> : null}
    </div>
  );

  const deleteBody = (
    <div className="configure-role-delete-body">
      Are you sure you want to delete
      {' '}
      <strong>{deleteConfirm?.row?.roleName || roleName || 'this role'}</strong>
      ?
    </div>
  );

  return (
    <React.Fragment>
      <FormDialog
        title="Configure Role"
        open={open}
        handleClose={onClose}
        button1Action={onClose}
        button2Action={handleSave}
        dialogBodycontent={body}
        button1="Cancel"
        button2="Save"
        cancelButtonId="configure-role-cancel"
        submitButtonId="configure-role-save"
        dialogCss="configure-role-dialog-root"
        dialogPaperCss="configure-role-dialog"
        button2Disabled={loading}
      />
      <FormDialog
        title="Delete Role"
        open={Boolean(deleteConfirm)}
        handleClose={() => setDeleteConfirm(null)}
        button1Action={() => setDeleteConfirm(null)}
        button2Action={handleConfirmDelete}
        dialogBodycontent={deleteBody}
        button1="Cancel"
        button2="Delete"
        cancelButtonId="configure-role-delete-cancel"
        submitButtonId="configure-role-delete-submit"
        dialogCss="configure-role-delete-dialog-root"
        dialogPaperCss="configure-role-delete-dialog"
        button2Disabled={loading}
      />
    </React.Fragment>
  );
};

ConfigureRoleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRoleId: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  userInfo: PropTypes.shape({}),
};

const RoleRatioReport = () => {
  const { userProfile } = useAuth();
  const [tableScrollHeight, setTableScrollHeight] = useState('500px');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [isConfigureOpen, setConfigureOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tableConfig, setTableConfig] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [useRemoteFilterOptions, setUseRemoteFilterOptions] = useState(true);
  const paginationRef = useRef({ page: 1, limit: PAGE_SIZE, total: 0 });
  const tableStateRef = useRef({ filters: [] });
  const activeFiltersRef = useRef([]);
  const tableConfigRef = useRef([]);
  const normalizedPermissions = useMemo(
    () => normalizeRoleRatioPermissions(userProfile?.permissions || {}),
    [userProfile?.permissions],
  );

  const roleUserInfo = useMemo(() => ({
    id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    emailId: userProfile?.email || userProfile?.emailId || '',
    email: userProfile?.email || userProfile?.emailId || '',
    permissions: normalizedPermissions,
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    role_details: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    emp_id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    empId: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    employeeId: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    employee_id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    loginType: userProfile?.login_type || userProfile?.loginType || userProfile?.Login_Type || '',
    login_type: userProfile?.login_type || userProfile?.loginType || userProfile?.Login_Type || '',
  }), [normalizedPermissions, userProfile]);

  const canEditRoleRatio = useMemo(() => {
    return hasRoleRatioAction(normalizedPermissions, ['update']);
  }, [normalizedPermissions]);

  const handleSaved = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
    setHasAppliedFilters(false);
  };

  const openConfigureRole = useCallback(() => {
    if (!canEditRoleRatio) return;
    setSelectedRoleId('');
    setConfigureOpen(true);
  }, [canEditRoleRatio]);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;
    const rawRows = response.data?.tableData || response.data?.data?.tableData || [];
    const responseTableConfig = response.data?.tableConfiguration
      || response.data?.data?.tableConfiguration
      || [];
    const activeFilters = getRoleRatioFiltersFromTableState(tableStateRef.current, activeFiltersRef.current);

    response.data = {
      ...response.data,
      tableData: rawRows,
      tableConfiguration: responseTableConfig,
      data: {
        ...(response.data?.data || {}),
        tableData: rawRows,
        tableConfiguration: responseTableConfig,
      },
    };

    const normalizedTableConfig = preserveRoleRatioFilterOptions(
      normalizeRoleRatioTableConfig(responseTableConfig, rawRows, useRemoteFilterOptions),
      tableConfigRef.current,
    );
    const nextTableConfig = mergeRoleRatioFilterSelections(
      normalizedTableConfig,
      activeFilters,
    );
    const paginationMeta = {
      page: response.meta?.page || response.data?.meta?.page || 1,
      limit: response.meta?.limit || response.data?.meta?.limit || PAGE_SIZE,
      total: response.meta?.total || response.data?.meta?.total || rawRows.length,
    };
    response.data.tableConfiguration = nextTableConfig;
    response.data.meta = paginationMeta;
    response.meta = paginationMeta;
    response.data.data.tableConfiguration = nextTableConfig;
    response.data.data.meta = paginationMeta;
    if (nextTableConfig.length) {
      tableConfigRef.current = nextTableConfig;
      setTableConfig(nextTableConfig);
    }
    setTableRows(rawRows);
    setHasAppliedFilters(hasRoleRatioRole1Filter(activeFilters));
  }, [useRemoteFilterOptions]);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    if (Array.isArray(filters)) {
      activeFiltersRef.current = filters;
    }

    if (action === 'reset') {
      setHasAppliedFilters(false);
      setTableRows([]);
      activeFiltersRef.current = [];
      tableStateRef.current = { ...tableStateRef.current, filters: [] };
      paginationRef.current = { page: 1, limit: PAGE_SIZE, total: 0 };
      return;
    }

    const hasRoleFilter = Array.isArray(filters) && filters.some((filter) => (
      filter?.field === 'role1Id'
      && (
        filter?.selectedValue
        || filter?.value
        || (Array.isArray(filter?.selectedValues) && filter.selectedValues.length)
      )
    ));

    setHasAppliedFilters(hasRoleFilter);
  }, []);

  const handleTableStateUpdate = useCallback((stateValue) => {
    const nextState = typeof stateValue === 'function' ? stateValue() : stateValue;
    if (!nextState || typeof nextState !== 'object') return;

    tableStateRef.current = nextState;
    if (Array.isArray(nextState.filters)) {
      activeFiltersRef.current = nextState.filters;
      setHasAppliedFilters(hasRoleRatioRole1Filter(nextState.filters));
    }
  }, []);

  const handleDownloadIconClick = useCallback(async () => {
    if (isExporting) return;

    const activeFilters = getRoleRatioFiltersFromTableState(tableStateRef.current, activeFiltersRef.current);
    if (!hasRoleRatioRole1Filter(activeFilters)) {
      toast.error('Please select Role 1 before exporting.');
      return;
    }

    setIsExporting(true);
    try {
      await exportRoleRatioWorkbook({
        filters: activeFilters,
        userInfo: roleUserInfo,
        requestExport: (payload) => UseAxios(exportRoleRatioReport(payload)),
        buildFileName: () => getExportFileName('Role Ratio'),
        onValidationError: (message) => toast.error(message),
        onEmpty: (message) => toast.error(message),
        onError: (message) => toast.error(message),
      });
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, roleUserInfo]);

  const toolbarAdditionalElements = useMemo(() => {
    const elements = [
      <button
        key="role-ratio-export"
        type="button"
        className="p-button p-component p-button-text download-excel-button table-utility-export-btn"
        onClick={handleDownloadIconClick}
        aria-label="Download Excel"
        title="Download Excel"
      >
        <DownloadExcelIcon aria-hidden="true" focusable="false" />
      </button>,
    ];

    if (canEditRoleRatio) {
      elements.push(
        <RoleRatioMoreActionsMenu key="role-ratio-more-actions" onViewRole={openConfigureRole} />,
      );
    }

    return elements;
  }, [canEditRoleRatio, handleDownloadIconClick, openConfigureRole]);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.7)}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadFullFilterConfig = async () => {
      const fullFilterResponse = await UseAxios(getRoleRatioFilters({ userInfo: roleUserInfo }));
      const fullTableConfig = fullFilterResponse?.data?.data?.tableConfiguration || [];
      if (!isMounted || !fullTableConfig.length) return false;

      const normalizedFullConfig = normalizeRoleRatioTableConfig(fullTableConfig, [], false);
      setUseRemoteFilterOptions(false);
      tableConfigRef.current = normalizedFullConfig;
      setTableConfig(normalizedFullConfig);
      return true;
    };

    const loadInitialFilters = async () => {
      const lightweightResponse = await UseAxios(getRoleRatioFilters({
        lightweight: true,
      }));
      const initialTableConfig = lightweightResponse?.data?.data?.tableConfiguration || [];
      if (isMounted && initialTableConfig.length) {
        const normalizedInitialConfig = normalizeRoleRatioTableConfig(initialTableConfig);
        tableConfigRef.current = normalizedInitialConfig;
        setTableConfig(normalizedInitialConfig);
      } else if (await loadFullFilterConfig()) {
        return;
      }

      const probeResponse = await UseAxios(getRoleRatioFilterOptions(
        ROLE_RATIO_FILTER_TYPES.projectTypes,
        roleUserInfo,
      ));
      if (!isMounted || !isMissingFilterOptionsRoute(probeResponse)) return;

      await loadFullFilterConfig();
    };

    loadInitialFilters();

    return () => {
      isMounted = false;
    };
  }, [roleUserInfo]);

  const tableConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      skipInitialFilteredDataFetch: true,
      baseURL,
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      showFilterOption: true,
      showColumnFilterMenu: true,
      headers: { token: userProfile?.access_token || '' },
      userInfo: roleUserInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Role Ratio'),
          sheetName: () => getExportFileNameWithDate('Role Ratio'),
          reportTitle: () => 'Role Ratio Report',
        },
      },
      handler: {
        handleFilteredApiResponse,
        onFilterUpdate: handleFilterUpdate,
        setTableStateHandler: handleTableStateUpdate,
      },
      additionalPayloadData: {},
      tableUtilityConfig: {
        tableConfig,
      },
      toolBarSettings: {
        showFilterOption: true,
        showColumnFilterMenu: true,
        showAddNew: false,
        showExpandCollapse: false,
        showDownloadExcel: false,
        downloadExcelSheetName: 'Role Ratio Report',
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Role Ratio Report',
        showReset: false,
        showFilters: true,
        additionalElements: toolbarAdditionalElements,
      },
    },
    tableSettings: {
      tableConfig,
      size: 'small',
      pageSize: PAGE_SIZE,
      tableScrollHeight,
      paginationConfig: {
        page: 1,
        limit: PAGE_SIZE,
        total: tableRows.length,
        rowsPerPageOptions: [PAGE_SIZE],
      },
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 4 },
      showExpandCollapse: false,
    },
    showFilterOption: true,
  }), [
    handleDownloadIconClick,
    handleFilteredApiResponse,
    handleFilterUpdate,
    handleTableStateUpdate,
    roleUserInfo,
    tableConfig,
    tableRows.length,
    tableScrollHeight,
    toolbarAdditionalElements,
    userProfile?.access_token,
  ]);

  return (
    <div className="role-ratio-page">
      <div className={`role-ratio-table table-utility ${!hasAppliedFilters ? 'role-ratio-empty' : ''}`}>
        {tableConfig.length > 0 && (
          <TableUtility
            configuration={tableConfiguration}
            data={tableRows}
            showFilterOption
          />
        )}
        {!hasAppliedFilters && <EmptyState message="Select the filters above to see the results" />}
      </div>
      <ConfigureRoleDialog
        open={isConfigureOpen}
        onClose={() => setConfigureOpen(false)}
        selectedRoleId={selectedRoleId}
        onSaved={handleSaved}
        userInfo={roleUserInfo}
      />
      <SimpleSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default RoleRatioReport;
